<template>
  <div class="location-container">
    <div class="location-box">
      <h2>Confirm Address</h2>
      <p>Type the address and pick the correct match — we'll save the real-world coordinates so your listing shows up on the right spot of the map.</p>

      <div class="input-group">
        <label for="houseNameOption">House Name</label>

        <div class="radio-option">
          <input type="radio" id="defaultName" v-model="houseNameOption" value="default" @change="setDefaultName" />
          <label for="defaultName">Use Default Name: {{ res.name }}</label>
        </div>

        <div class="radio-option">
          <input type="radio" id="customName" v-model="houseNameOption" value="custom" @change="setCustomName" />
          <label for="customName">Use Custom Name</label>
        </div>

        <input
          type="text"
          id="houseName"
          v-model="houseName"
          :placeholder="houseNameOption === 'default' ? 'Default name: ' + res.name : 'Enter custom house name'"
          :disabled="houseNameOption === 'default'"
          v-if="houseNameOption === 'custom'"
        />
      </div>

      <div class="input-group address-group">
        <label for="addressSearch">Address</label>
        <div class="autocomplete-wrapper">
          <input
            id="addressSearch"
            type="text"
            v-model="addressQuery"
            @input="onAddressInput"
            @focus="showDropdown = suggestions.length > 0"
            placeholder="Start typing an address, e.g. Clear Water Bay Road, Sai Kung"
            autocomplete="off"
          />
          <div class="autocomplete-dropdown" v-if="showDropdown && (suggestions.length > 0 || searching)">
            <div class="autocomplete-status" v-if="searching">Searching…</div>
            <div
              v-for="s in suggestions"
              :key="s.place_id"
              class="autocomplete-item"
              @click="pickSuggestion(s)"
            >
              <div class="autocomplete-primary">{{ s.display_name }}</div>
              <div class="autocomplete-secondary" v-if="s.type || s.class">
                {{ s.type || s.class }}
              </div>
            </div>
          </div>
        </div>
        <p class="hint" v-if="selected">
          📍 Selected: <strong>{{ selected.display_name }}</strong>
          <span class="coords">({{ Number(selected.lat).toFixed(5) }}, {{ Number(selected.lon).toFixed(5) }})</span>
        </p>
      </div>

      <button class="submit-button" @click="presubmitLocation" :disabled="!selected">Submit Location</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  data() {
    return {
      res: { name: '' },
      houseId: 0,
      houseLetId: 0,
      houseName: '',
      houseNameOption: 'default',
      addressQuery: '',
      suggestions: [],
      selected: null,
      searching: false,
      showDropdown: false,
      debounceTimer: null,
    };
  },
  created() {
    const houseId = this.$route.query.houseId;
    const houseLetId = this.$route.query.houseLetId;
    this.houseId = houseId;
    this.houseLetId = houseLetId;
    this.getHouseInfo(this, this.houseId);
  },
  methods: {
    getHouseInfo(obj, house) {
      axios.get('/api/house', { params: { houseId: house } })
        .then((response) => {
          obj.res = response.data.data || { name: '' };
          obj.houseName = obj.res.name || '';
          // Seed the autocomplete with whatever address fragments we have
          const addressParts = [
            obj.res.street,
            obj.res.detailedArea,
            obj.res.area,
            obj.res.district,
            obj.res.region,
            'Hong Kong'
          ].filter(Boolean);
          if (addressParts.length > 0) {
            obj.addressQuery = addressParts.join(', ');
            obj.search(obj.addressQuery);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    onAddressInput() {
      this.selected = null;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      if (!this.addressQuery || this.addressQuery.trim().length < 3) {
        this.suggestions = [];
        this.showDropdown = false;
        return;
      }
      this.debounceTimer = setTimeout(() => this.search(this.addressQuery), 400);
    },
    search(query) {
      this.searching = true;
      this.showDropdown = true;
      // Hong Kong Government Address Lookup Service — free, no API key, and
      // parses local addresses (estates, towers, villages) that generic
      // geocoders miss. Each result is mapped into the {display_name, lat,
      // lon, place_id} shape pickSuggestion() expects.
      const params = new URLSearchParams({ q: query, n: '6' }).toString();
      fetch(`https://www.als.gov.hk/lookup?${params}`, {
        headers: { 'Accept': 'application/json' },
      })
        .then(r => r.json())
        .then((data) => {
          const list = Array.isArray(data?.SuggestedAddress) ? data.SuggestedAddress : [];
          this.suggestions = list
            .map((item) => this._mapAlsSuggestion(item))
            .filter((s) => s !== null);
          this.searching = false;
        })
        .catch(() => {
          this.suggestions = [];
          this.searching = false;
        });
    },
    _mapAlsSuggestion(item) {
      const premises = item?.Address?.PremisesAddress;
      const eng = premises?.EngPremisesAddress;
      if (!premises || !eng) return null;

      const lat = premises.GeospatialInformation?.Latitude;
      const lon = premises.GeospatialInformation?.Longitude;
      if (lat === undefined || lat === null || lat === '' || lon === undefined || lon === null || lon === '') {
        return null;
      }

      // ALS components vary by suggestion type (estate flat, single street
      // address, village house, commercial building, university hall). Every
      // field is optional and defensively joined.
      const block = [eng.EngBlock?.BlockDescriptor, eng.EngBlock?.BlockNo]
        .filter(Boolean).join(' ').trim();
      const buildingName = (eng.BuildingName || eng.EngBuilding?.BuildingName || '').trim();
      const estate = [eng.EngEstate?.EstateName, eng.EngEstate?.EngPhase?.PhaseName]
        .filter(Boolean).join(' ').trim();
      const street = [eng.EngStreet?.BuildingNoFrom, eng.EngStreet?.StreetName]
        .filter(Boolean).join(' ').trim();
      const location = (eng.EngStreet?.LocationName || '').trim();
      const district = (eng.EngDistrict?.DcDistrict || '').trim();
      const region = (eng.Region || '').trim();

      const displayName = [block, buildingName, estate, street, location, district, region]
        .filter(Boolean)
        .join(', ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!displayName) return null;

      return {
        display_name: displayName,
        lat,
        lon,
        place_id: premises.GeoAddress || `${lat},${lon}`,
        _als: item,
      };
    },
    pickSuggestion(s) {
      this.selected = s;
      this.addressQuery = s.display_name;
      this.showDropdown = false;
    },
    setDefaultName() {
      this.houseName = this.res.name;
    },
    setCustomName() {
      this.houseName = '';
    },
    presubmitLocation() {
      this.submitLocation(this);
    },
    submitLocation(obj) {
      if (!this.selected) {
        Swal.fire({
          icon: 'warning',
          title: 'Address Required',
          text: 'Please search for and select an address from the suggestions.',
          confirmButtonColor: '#3bc2ff'
        });
        return;
      }

      if (this.houseNameOption === 'custom' && !this.houseName) {
        Swal.fire({
          icon: 'warning',
          title: 'Name Required',
          text: 'Custom house name cannot be empty.',
          confirmButtonColor: '#3bc2ff'
        });
        return;
      }

      const locationData = {
        lat: parseFloat(this.selected.lat),
        lon: parseFloat(this.selected.lon),
        houseId: this.houseId,
        houseLetId: this.houseLetId,
        houseName: this.houseName
      };

      axios.post('/api/location/save', locationData)
        .then(response => {
          console.log(response);
          obj.ToHouseReviewScore();
        })
        .catch(error => {
          Swal.fire({
            icon: 'error',
            title: 'Submission Failed',
            text: 'Failed to submit location. Please try again.',
            confirmButtonColor: '#3bc2ff'
          });
          console.error(error);
        });
    },
    ToHouseReviewScore() {
      if (this.$route.path !== '/HouseReviewScore') {
        this.$router.push({ path: '/HouseReviewScore', query: { houseId: this.houseId, houseLetId: this.houseLetId } });
      }
    },
  },
};
</script>

<style>
.location-container {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: 85vh;
  background: white;
  padding: 24px 24px 48px;
}

.location-box {
  background: white;
  padding: 32px;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04);
  width: 800px;
  max-width: 100%;
}

.location-box h2 {
  margin-bottom: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
}

.location-box p {
  margin-bottom: 24px;
  color: #64748b;
  font-size: 0.95rem;
}

.input-group {
  margin-bottom: 20px;
}

.input-group label {
  display: inline-block;
  margin-right: 10px;
  font-weight: bold;
}

.radio-option {
  margin-bottom: 10px;
}

.radio-option input[type="radio"] {
  margin-right: 5px;
}

.input-group input[type="text"] {
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  font-size: 1rem;
  color: #0f172a;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
  box-sizing: border-box;
}

.input-group input[type="text"]:focus {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 3px rgba(59, 194, 255, 0.1);
}

.input-group input[type="text"]:disabled {
  background: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.address-group {
  position: relative;
}

.autocomplete-wrapper {
  position: relative;
}

.autocomplete-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  /* High z-index to clear mobile virtual keyboard stacking context */
  z-index: 9999;
  max-height: 320px;
  overflow-y: auto;
}

.autocomplete-status {
  padding: 12px 16px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.autocomplete-item {
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item:hover {
  background: #f0f9ff;
}

.autocomplete-primary {
  font-size: 0.95rem;
  color: #0f172a;
  line-height: 1.35;
}

.autocomplete-secondary {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
  text-transform: capitalize;
}

.hint {
  margin-top: 10px;
  padding: 10px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #065f46;
}

.hint .coords {
  color: #047857;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  margin-left: 6px;
}

.submit-button {
  background: linear-gradient(135deg, #3bc2ff 0%, #0284c7 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(2, 132, 199, 0.3);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
